// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './AppStore.css';
import BlogHeaderAd from '../Blog/BlogHeaderAd';
import LogoLoader from '../components/LogoLoader';

const APPS = [
    {
        id: 'pomodoro',
        name: 'Pomodoro',
        description: 'Focus timer for deep work and productivity sessions',
        iconClass: 'pomodoro-icon',
        icon: 'fi fi-rr-clock',
        path: '/app-store/pomodoro',
        requiresAuth: false,
        themeColor: '#FF6A88',
    },
    {
        id: 'typewriting',
        name: 'Typewriting',
        description: 'Practice and improve your typing speed with guided lessons and tests',
        iconClass: 'typewriting-icon',
        icon: 'fi fi-rr-keyboard',
        path: '/app-store/typewriting',
        requiresAuth: true,
        themeColor: '#00b894',
    },
    {
        id: 'bookstore',
        name: 'Book Store',
        description: 'Explore and discover amazing digital books and resources',
        iconClass: 'bookstore-icon',
        icon: 'fi fi-rr-book',
        path: '/app-store/bookstore',
        requiresAuth: false,
        themeColor: '#d35400',
    },
];

function AppStore() {
    const navigate = useNavigate();
    const [hasHeaderAd, setHasHeaderAd] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchFocused, setSearchFocused] = useState(false);
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'

    // Logo Loader State
    const [showLoader, setShowLoader] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);

    // Initial loading effect
    useEffect(() => {
        let progress = 0;
        const interval = setInterval(() => {
            if (progress < 100) {
                const increment = Math.random() * 20;
                progress = Math.min(progress + increment, 100);
                setLoadingProgress(progress);
            } else {
                clearInterval(interval);
                setTimeout(() => setShowLoader(false), 500);
            }
        }, 100);

        return () => clearInterval(interval);
    }, []);

    const handleAppClick = (app) => {
        if (app.strictAuth) {
            const user = sessionStorage.getItem("user");
            if (!user) {
                navigate('/signin', { state: { from: { pathname: app.path } } });
                return;
            }
        } else if (app.requiresAuth) {
            const user = sessionStorage.getItem("user");
            const token = localStorage.getItem("token") || sessionStorage.getItem("token");
            if (!user && !token) {
                navigate('/signin', { state: { from: { pathname: app.path } } });
                return;
            }
        }
        navigate(app.path);
    };

    const filteredApps = useMemo(() => {
        let list = [...APPS];
        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            list = list.filter(a => a.name.toLowerCase().includes(q));
        }
        list.sort((a, b) =>
            sortOrder === 'asc'
                ? a.name.localeCompare(b.name)
                : b.name.localeCompare(a.name)
        );
        return list;
    }, [searchQuery, sortOrder]);

    const appStoreSchema = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "App Store – Free Web Tools by Hari Vishnu",
        "description": "Free productivity and creative web tools by Hari Vishnu. Includes Pomodoro Timer, Color Palette Generator, and more.",
        "url": "https://apps.harivishnu.in",
        "author": {
            "@type": "Person",
            "name": "Hari Vishnu",
            "url": "https://www.harivishnu.in"
        }
    };

    if (showLoader) {
        return <LogoLoader progress={loadingProgress} />;
    }

    return (
        <>
            <Helmet>
                <title>App Store – Free Web Tools | Hari Vishnu</title>
                <meta name="description" content="Free productivity and creative web tools by Hari Vishnu. Try the Pomodoro Timer, Color Palette Generator, and more — no sign-up required." />
                <meta name="keywords" content="pomodoro timer, color palette generator, free web tools, productivity tools, Hari Vishnu" />
                <link rel="canonical" href="https://apps.harivishnu.in" />
                <meta property="og:type" content="website" />
                <meta property="og:title" content="App Store – Free Web Tools | Hari Vishnu" />
                <meta property="og:description" content="Free productivity and creative web tools by Hari Vishnu. Pomodoro Timer, Color Palette Generator, and more." />
                <meta property="og:url" content="https://apps.harivishnu.in" />
                <meta property="og:image" content="https://www.harivishnu.in/images/home_images/Logo.png" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="App Store – Free Web Tools | Hari Vishnu" />
                <meta name="twitter:description" content="Free productivity and creative web tools by Hari Vishnu." />
                <meta name="twitter:image" content="https://www.harivishnu.in/images/home_images/Logo.png" />
                <script type="application/ld+json">{JSON.stringify(appStoreSchema)}</script>
            </Helmet>

            <div className="appstore-header mx-auto mt-[1rem] w-[min(95%,1200px)]">
                <div className={`appstore-header-top-row flex items-start justify-between gap-[16px] ${hasHeaderAd ? '' : ' no-active-ad'}`}>
                    <h1 className={`appstore-header-title text-[clamp(1.8rem,3vw,2.8rem)] font-[800] text-post-heading ${hasHeaderAd ? '' : ' centered text-center w-full'}`}>App Store</h1>
                    <div className={`appstore-header-ad-wrapper shrink-0 ${hasHeaderAd ? '' : ' is-hidden hidden'}`}>
                        <BlogHeaderAd onAvailabilityChange={setHasHeaderAd} />
                    </div>
                </div>
                <div className="appstore-header-controls mt-[1rem] flex flex-wrap items-center gap-[12px]">
                    <div className={`appstore-search-wrapper flex-1 min-w-[280px] ${searchFocused ? ' is-focused' : ''}`}>
                        <div className={`appstore-search-inner flex items-center gap-[8px] rounded-[999px] border bg-bg px-[14px] py-[10px] ${searchFocused ? 'border-primary-cyan shadow-[0_0_0_3px_rgba(71,181,255,0.15)]' : 'border-slider'}`}>
                            <i className="fi fi-rr-search text-[#7a7a7a]"></i>
                            <span className="appstore-search-sep h-[18px] w-[1px] bg-slider"></span>
                            <input
                                type="text"
                                className="appstore-search-input min-w-0 flex-1 border-none bg-transparent outline-none"
                                placeholder="Search apps..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                onFocus={() => setSearchFocused(true)}
                                onBlur={() => setSearchFocused(false)}
                            />
                            {searchQuery && (
                                <button
                                    className="appstore-search-clear flex h-[28px] w-[28px] items-center justify-center rounded-full border-none bg-transparent text-[#666] hover:bg-black/5"
                                    onClick={() => setSearchQuery('')}
                                    aria-label="Clear search"
                                    type="button"
                                >
                                    <i className="fi fi-rr-cross-small"></i>
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="appstore-sort-container flex items-center gap-[8px] rounded-[10px] border border-slider bg-bg px-[10px] py-[6px]">
                        <span className="appstore-sort-label text-[0.82rem] font-[600] text-[#666]">Sort:</span>
                        <span className="appstore-sort-name text-[0.9rem] font-[700] text-text">Name</span>
                        <button
                            className="appstore-sort-btn ml-[2px] flex items-center gap-[4px] rounded-[8px] border-none bg-transparent px-[6px] py-[3px] text-primary-cyan hover:bg-black/5"
                            type="button"
                            onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
                            title={sortOrder === 'asc' ? 'A–Z' : 'Z–A'}
                        >
                            <i className={`fi fi-rr-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                            <span className="appstore-sort-letter text-[0.75rem] font-[800]">{sortOrder === 'asc' ? 'A' : 'Z'}</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="appstore-container mx-auto my-[1rem] w-[min(95%,1200px)]">
                <div className="appstore-grid grid gap-[14px] md:grid-cols-2 xl:grid-cols-3">
                    {filteredApps.length === 0 ? (
                        <div className="appstore-no-results col-span-full flex flex-col items-center justify-center rounded-[14px] border border-slider bg-bg py-[28px] text-center">
                            <i className="fi fi-rr-search"></i>
                            <p>No apps found{searchQuery.trim() ? ` for "${searchQuery.trim()}"` : ''}.</p>
                        </div>
                    ) : (
                        <>
                            {filteredApps.map((app) => (
                                <div
                                    key={app.id}
                                    className="appstore-card flex h-auto items-center gap-[12px] rounded-[14px] border border-slider bg-bg p-[14px]"
                                    style={{
                                        '--app-theme-color': app.themeColor
                                    }}
                                >
                                    <div className={`appstore-app-icon ${app.iconClass} flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-[12px]`}>
                                        <i className={app.icon}></i>
                                    </div>
                                    <div className="appstore-app-info min-w-0 flex-1">
                                        <span className="appstore-app-name block text-[1rem] font-[700] text-post-heading">{app.name}</span>
                                        <span className="appstore-app-desc mt-[2px] block text-[0.9rem] text-[#666]">{app.description}</span>
                                    </div>
                                    <button
                                        className="appstore-view-btn shrink-0 rounded-[10px] border border-slider bg-bg px-[12px] py-[8px] font-[700] text-text hover:border-primary-cyan"
                                        onClick={() => handleAppClick(app)}
                                        type="button"
                                    >
                                        {!(app.requiresAuth || app.strictAuth) && <span className="appstore-view-text">View</span>}
                                        <i className={`fi ${(app.requiresAuth || app.strictAuth) ? 'fi-sr-user appstore-view-icon--auth' : 'fi-sr-arrow-small-up appstore-view-icon--arrow'} appstore-view-icon`}></i>
                                    </button>
                                </div>
                            ))}

                            {/* Placeholder cells to fill the last row and show grid lines */}
                            {filteredApps.length % 3 !== 0 &&
                                Array.from({ length: 3 - (filteredApps.length % 3) }).map((_, i) => (
                                    <div key={`ph-${i}`} className="appstore-card appstore-card-placeholder" />
                                ))
                            }
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default AppStore;
